import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Customer interface
export interface Customer {
  _id: string;
  mobileNo: string;
  name: string;
  pan: string;
  dob: string;
  email?: string;
  employmentType?: number;
  monthlyIncome?: number;
  preApprovedAmount: number;
  preApprovedMaxMatchLender: string | null;
  platform: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  preApprovedResponse?: {
    [key: string]: {
      error?: string;
      timestamp?: string;
    }
  };
  serialNumber?: number; // Added by the frontend
}

// Customer log entry interface
export interface CustomerLog {
  srNo: number;
  timestamp: string;
  cashe?: {
    amount: number;
    status: string;
  };
  zype?: {
    status?: string;
    message?: string;
    offer?: number;
    error?: string;
    timestamp?: string;
  };
  [key: string]: any; // For other lenders
}

// Customer detail response interface
export interface CustomerDetailResponse {
  status?: string;
  result: {
    customer: Customer;
    logs: CustomerLog[];
    platform: string;
    merchant?: {
      name: string;
      email: string;
      mobileNo: string;
      afpId: string;
    };
    partner?: {
      name: string;
      mobileNo: string;
      email: string;
      isEv: boolean;
      isBrand: boolean;
      lender: string;
    };
  }
}

// API response interface
export interface CustomerApiResponse {
  status: string;
  result: {
    data: {
      totalCount: number;
      totalMatch: number;
      totalPages: number;
      pageNo: number;
      pageSize: number;
      results: Customer[];
    }
  }
}

// Filter type
export interface CustomerFilters {
  platform?: string;
  partner? : string;
  dateRange?: {
    from?: string;
    to?: string;
  };
}

// Helper function to format date for API
const formatDateForAPI = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
};

// Fetch parameters interface
export interface FetchCustomersParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  token?: string | null;
  filters?: CustomerFilters;
}

/**
 * Fetch customers with pagination, sorting, and search
 */
export const fetchCustomers = async ({
  page,
  pageSize,
  sortField,
  sortOrder,
  search,
  token,
  filters = {}
}: FetchCustomersParams): Promise<{
  customers: Customer[];
  totalCount: number;
  totalMatch: number;
  totalPages: number;
  pageNo: number;
  pageSize: number;
}> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }
    
    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    
    // Add sort parameters if provided
    if (sortField && sortOrder) {
      params.append("sortBy", sortField);
      params.append("sortOrder", sortOrder);
    }
    
    // Add search parameter if provided
    if (search) {
      params.append("searchQuery", search);
    }
    
    // Add filter parameters
    if (filters.platform) {
      params.append("platform", filters.platform);
    }

    if (filters.partner) {
      params.append("partner", filters.partner);
    }
    
    // Date filters use 'from' and 'to' params in DD-MM-YYYY format
    if (filters.dateRange?.from) {
      params.append("from", formatDateForAPI(filters.dateRange.from));
    }
    if (filters.dateRange?.to) {
      params.append("to", formatDateForAPI(filters.dateRange.to));
    }
    
    console.log(`[API] Fetching customers with filters:`, {
      page,
      pageSize,
      sortField,
      sortOrder,
      search,
      filters,
      formattedDates: {
        from: filters.dateRange?.from ? formatDateForAPI(filters.dateRange.from) : undefined,
        to: filters.dateRange?.to ? formatDateForAPI(filters.dateRange.to) : undefined
      }
    });
    
    const url = `/adminusers/customer-user?${params.toString()}`;
    console.log(`[API] Fetching customers with URL: ${url}`);
    
    const response = await axios.get<CustomerApiResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });
    
    // Check if response is valid
    if (response.data?.status !== "Request successful!" || !response.data.result) {
      throw new Error("Unexpected API response structure");
    }
    
    const { totalCount, totalMatch, totalPages, pageNo, pageSize: returnedPageSize, results } = response.data.result.data;
    
    // Add serial numbers to customers
    const customersWithSerialNumbers = results.map((customer, index) => ({
      ...customer,
      serialNumber: (page - 1) * pageSize + index + 1
    }));
    
    return {
      customers: customersWithSerialNumbers,
      totalCount,
      totalMatch,
      totalPages,
      pageNo,
      pageSize: returnedPageSize
    };
  } catch (error: any) {
    console.error('Error fetching customers:', error);
    showToast.error(error.message || 'Failed to fetch customers');
    throw error;
  }
};

/**
 * Fetch customer details by mobile number
 */
export const fetchCustomerDetails = async (
  mobileNo: string,
  token?: string | null
): Promise<CustomerDetailResponse> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }
    
    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }
    
    // Check if mobile number is provided
    if (!mobileNo) {
      throw new Error("Mobile number is required");
    }
    
    const url = `/adminusers/customer-user?mobileNo=${mobileNo}`;
    console.log(`[API] Fetching customer details with URL: ${url}`);
    
    const response = await axios.get<CustomerDetailResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });
    
    // Check if response is valid
    if (!response.data || !response.data.result || !response.data.result.customer) {
      throw new Error("Unexpected API response structure");
    }
    
    return response.data;
  } catch (error: any) {
    console.error('Error fetching customer details:', error);
    showToast.error(error.message || 'Failed to fetch customer details');
    throw error;
  }
}; 